// =============================================================================
// Refunds.js, /refunds (Refund Policy)
//
// Phase 38.7, Tightened to the founder's authoritative stance:
//   "No refunds. Cancellation takes effect at the end of the paid term;
//    access continues until then; no further charges after that."
//
// We retain a brief carve-out for mandatory consumer-protection refunds
// (some jurisdictions require these regardless of policy) and for
// clearly-exceptional cases like duplicate billing caused by a bug.
// =============================================================================
import React from 'react';
import LegalPage from '@/components/LegalPage';
import usePageTitle from '@/hooks/usePageTitle';

export default function Refunds() {
  usePageTitle('Refund Policy');
  const sections = [
    {
      heading: 'Our refund stance, in one sentence',
      body: '2120 does not issue refunds. When you cancel, your subscription continues through the end of the term you have already paid for, and you are not charged again.',
    },
    {
      heading: 'Why we don’t do partial refunds',
      body: 'We offer a 14-day free trial on Horizon and Summit, no payment method required, specifically so you can evaluate the Service before committing any money. A generous trial up front is more honest than a permissive refund policy on the back end, and it lets us keep our prices low. The Clarity plan is intentionally priced low for the same reason.',
    },
    {
      heading: 'How cancellation works',
      body: 'You can cancel your subscription at any time from your account settings. When you cancel:',
      list: [
        'Your subscription remains active until the end of the term you have already paid for.',
        'You will not be charged again at the next renewal date.',
        'Your data remains in your account on the Free plan; you can export it or re-subscribe at any time.',
      ],
    },
    {
      heading: 'Mandatory consumer-protection refunds',
      body: 'If consumer-protection law in your country of residence requires us to issue a refund in particular circumstances (for example, a statutory cooling-off period), we will honour that obligation. To request a refund on this basis, email privacy@2120.money within the legally required window with your account email and a brief description of the situation.',
    },
    {
      heading: 'Exceptional refunds',
      body: 'At our sole discretion we may issue a refund in clearly exceptional cases, for example, accidental double-charging caused by a bug on our side, or a fraudulent unauthorised charge that our payment processors have confirmed. Discretionary refunds are not a precedent; they are case-by-case acts of fairness.',
    },
    {
      heading: 'Chargebacks',
      body: 'If you have a billing concern, please contact us first, we are usually able to resolve it within one business day. Initiating a chargeback through your bank without contacting us may result in immediate suspension of your account while the dispute is investigated.',
    },
    {
      heading: 'Taxes',
      body: 'Where applicable taxes (GST, VAT, sales tax) have been collected on a charge, those taxes follow the underlying refund. If the underlying charge is not refunded, the taxes are also not refunded.',
    },
    {
      heading: 'Contact',
      body: 'Questions about this policy or a specific charge? Email privacy@2120.money from the address on your account. Please include the invoice number or the last four digits of the card used.',
    },
  ];

  return (
    <LegalPage
      title="Refund Policy"
      subtitle="Calm and honest: we don’t issue refunds, and our 14-day no-card trial is our promise that you can try before you buy."
      lastUpdated="May 31, 2026"
      sections={sections}
    />
  );
}
